import React, { useState } from 'react';
import { Zap, Flame, Building, Home, Send, Check } from 'lucide-react';

interface FormData {
  vertragsart: string;
  kundentyp: string;
  name: string;
  email: string;
  telefon: string;
  plz: string;
  stadt: string;
  strasse: string;
  verbrauch: string;
}

export default function StromGasFormular() {
  const [formData, setFormData] = useState<FormData>({
    vertragsart: 'Strom',
    kundentyp: 'Privat',
    name: '',
    email: '',
    telefon: '',
    plz: '',
    stadt: '',
    strasse: '',
    verbrauch: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (submitError) setSubmitError(null);
  };

  const validateForm = () => {
    if (!formData.name || formData.name.length < 2) {
      return 'Name ist erforderlich (mindestens 2 Zeichen)';
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Gültige E-Mail-Adresse ist erforderlich';
    }
    if (!formData.telefon || formData.telefon.length < 10) {
      return 'Telefonnummer ist erforderlich (mindestens 10 Zeichen)';
    }
    if (!formData.plz || formData.plz.length !== 5) {
      return 'Postleitzahl muss 5-stellig sein';
    }
    if (!formData.verbrauch || parseInt(formData.verbrauch) <= 0) {
      return 'Verbrauch muss eine positive Zahl sein';
    }
    if (!formData.stadt || formData.stadt.length < 2) {
      return 'Stadt ist erforderlich';
    }
    if (!formData.strasse || formData.strasse.length < 3) {
      return 'Straße & Hausnummer ist erforderlich';
    }
    if (!acceptPrivacy) {
      return 'Bitte akzeptieren Sie die Datenschutzerklärung';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Validierung
    const validationError = validateForm();
    if (validationError) {
      setSubmitError(validationError);
      setIsSubmitting(false);
      return;
    }

    try {
      // Erstelle WhatsApp-Nachricht direkt
      const typeEmoji = formData.vertragsart === 'Strom' ? '⚡' : '🔥';
      const customerTypeEmoji = formData.kundentyp === 'Privat' ? '🏠' : '🏢';
      const timestamp = new Date().toLocaleString('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });

      const whatsappMessage = `
🔌⚡ NEUE STROM & GAS ANFRAGE

👤 KUNDE:
${formData.name}
📧 ${formData.email}
📞 ${formData.telefon}

📋 Typ: ${typeEmoji} ${formData.vertragsart.toUpperCase()}
🏠 Kundentyp: ${customerTypeEmoji} ${formData.kundentyp.toUpperCase()}

📍 ADRESSE:
${formData.strasse}
${formData.plz} ${formData.stadt}

📊 VERBRAUCH: ${parseInt(formData.verbrauch).toLocaleString('de-DE')} kWh

⏰ ${timestamp}
🌐 Easy Vertrag Website
      `.trim();

      // WhatsApp-Link erstellen
      const phoneNumber = '491797473491';
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;

      // WhatsApp öffnen
      window.open(whatsappUrl, '_blank');

      // Erfolg anzeigen
      setIsSubmitted(true);

      // Form nach 5 Sekunden zurücksetzen
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          vertragsart: 'Strom',
          kundentyp: 'Privat',
          name: '',
          email: '',
          telefon: '',
          plz: '',
          stadt: '',
          strasse: '',
          verbrauch: '',
        });
        setAcceptPrivacy(false);
      }, 5000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Ein unbekannter Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-xl mx-auto p-6 border rounded-xl bg-white shadow-md text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Anfrage erfolgreich gesendet!</h3>
        <p className="text-gray-600 mb-4">
          Ihre {formData.vertragsart} Anfrage wurde erfolgreich übermittelt.
        </p>
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <ul className="text-left text-sm text-blue-800 space-y-2">
            <li>✅ WhatsApp-Nachricht wurde geöffnet</li>
            <li>✅ Ihre Daten wurden übermittelt</li>
            <li>✅ Persönliche Beratung innerhalb von 24h</li>
            <li>✅ Individuelle Tarifempfehlungen</li>
          </ul>
        </div>
        <p className="text-sm text-gray-500">
          Das Formular wird automatisch zurückgesetzt...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto p-6 border rounded-xl bg-white shadow-md">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="flex space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Flame className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Strom & Gas Anfrage</h2>
        <p className="text-gray-600 mt-2">Finden Sie die besten Tarife für Ihre Bedürfnisse</p>
      </div>

      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">
            <strong>Fehler:</strong> {submitError}
          </p>
        </div>
      )}

      {/* Vertragsart */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Was benötigen Sie? *
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, vertragsart: 'Strom' })}
            className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 transition-all ${
              formData.vertragsart === 'Strom' 
                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Zap className="w-5 h-5" />
            <span className="font-medium">Strom</span>
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, vertragsart: 'Gas' })}
            className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 transition-all ${
              formData.vertragsart === 'Gas' 
                ? 'border-orange-500 bg-orange-50 text-orange-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Flame className="w-5 h-5" />
            <span className="font-medium">Gas</span>
          </button>
        </div>
      </div>

      {/* Kundentyp */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Kundentyp *
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, kundentyp: 'Privat' })}
            className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 transition-all ${
              formData.kundentyp === 'Privat' 
                ? 'border-green-500 bg-green-50 text-green-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">Privat</span>
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, kundentyp: 'Gewerbe' })}
            className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 transition-all ${
              formData.kundentyp === 'Gewerbe' 
                ? 'border-purple-500 bg-purple-50 text-purple-700' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Building className="w-5 h-5" />
            <span className="font-medium">Gewerbe</span>
          </button>
        </div>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Vollständiger Name *
        </label>
        <input 
          type="text" 
          id="name"
          name="name" 
          placeholder="z.B. Max Mustermann" 
          value={formData.name} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
        />
      </div>

      {/* E-Mail */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          E-Mail-Adresse *
        </label>
        <input 
          type="email" 
          id="email"
          name="email" 
          placeholder="z.B. max.mustermann@email.de" 
          value={formData.email} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
        />
      </div>

      {/* Telefon */}
      <div>
        <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-1">
          Telefonnummer *
        </label>
        <input 
          type="tel" 
          id="telefon"
          name="telefon" 
          placeholder="z.B. +49 179 1234567" 
          value={formData.telefon} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
        />
      </div>

      {/* Postleitzahl */}
      <div>
        <label htmlFor="plz" className="block text-sm font-medium text-gray-700 mb-1">
          Postleitzahl *
        </label>
        <input 
          type="text" 
          id="plz"
          name="plz" 
          placeholder="z.B. 66333" 
          value={formData.plz} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
          maxLength={5}
        />
      </div>

      {/* Stadt */}
      <div>
        <label htmlFor="stadt" className="block text-sm font-medium text-gray-700 mb-1">
          Stadt *
        </label>
        <input 
          type="text" 
          id="stadt"
          name="stadt" 
          placeholder="z.B. Völklingen" 
          value={formData.stadt} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
        />
      </div>

      {/* Straße & Hausnummer */}
      <div>
        <label htmlFor="strasse" className="block text-sm font-medium text-gray-700 mb-1">
          Straße & Hausnummer *
        </label>
        <input 
          type="text" 
          id="strasse"
          name="strasse" 
          placeholder="z.B. Bismarck Straße 31" 
          value={formData.strasse} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
        />
      </div>

      {/* Verbrauch */}
      <div>
        <label htmlFor="verbrauch" className="block text-sm font-medium text-gray-700 mb-1">
          Jahresverbrauch in kWh *
        </label>
        <input 
          type="number" 
          id="verbrauch"
          name="verbrauch" 
          placeholder={formData.vertragsart === 'Strom' ? 'z.B. 3500' : 'z.B. 15000'}
          value={formData.verbrauch} 
          onChange={handleChange} 
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" 
          required 
          min="1"
        />
        <p className="mt-1 text-sm text-gray-500">
          {formData.vertragsart === 'Strom' 
            ? 'Durchschnittlicher Stromverbrauch: 1-Person: 2000 kWh, 4-Personen: 4000 kWh'
            : 'Durchschnittlicher Gasverbrauch: Wohnung: 12000 kWh, Einfamilienhaus: 20000 kWh'
          }
        </p>
      </div>

      {/* DSGVO Checkbox */}
      <div className="flex items-start">
        <input
          id="privacy"
          type="checkbox"
          checked={acceptPrivacy}
          onChange={(e) => setAcceptPrivacy(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
          required
        />
        <label htmlFor="privacy" className="ml-2 text-sm text-gray-600">
          Ich akzeptiere die <a href="/datenschutz" className="text-blue-600 hover:underline" target="_blank">Datenschutzerklärung</a> und stimme zu, dass meine Daten zur Bearbeitung meiner Anfrage gespeichert werden. *
        </label>
      </div>

      {/* Submit Button */}
      <button 
        type="submit" 
        disabled={isSubmitting}
        className={`w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
          isSubmitting 
            ? 'bg-gray-400 text-white cursor-not-allowed' 
            : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
        }`}
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            <span>Wird gesendet...</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>Anfrage senden</span>
          </>
        )}
      </button>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Hinweis:</strong> Ihre Anfrage wird direkt per WhatsApp an unser Team gesendet. 
          Wir werden uns schnellstmöglich mit einem passenden Angebot bei Ihnen melden.
        </p>
      </div>
    </form>
  );
}